import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";

interface HidePricesContextType {
  hidePrices: boolean;
  toggleHidePrices: () => void;
}

const HidePricesContext = createContext<HidePricesContextType | undefined>(undefined);

export function useHidePrices() {
  const ctx = useContext(HidePricesContext);
  if (!ctx) throw new Error("useHidePrices must be used within HidePricesProvider");
  return ctx;
}

export function HidePricesProvider({ children }: { children: ReactNode }) {
  const [hidePrices, setHidePrices] = useState(() => sessionStorage.getItem("hidePrices") === "true");

  const toggleHidePrices = useCallback(() => {
    setHidePrices((prev) => {
      const next = !prev;
      sessionStorage.setItem("hidePrices", String(next));
      return next;
    });
  }, []);

  // Sync across tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === "hidePrices") {
        setHidePrices(e.newValue === "true");
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  return (
    <HidePricesContext.Provider value={{ hidePrices, toggleHidePrices }}>
      {children}
    </HidePricesContext.Provider>
  );
}
